import { useEffect, useState } from "react";
import { AdminApi, type DashboardStats } from "@/api/adminApi";
import { RestClient } from "@/api/restClient";

const apiUrl = import.meta.env.VITE_API_URL || "";
const client = new RestClient({ apiUrl });
const adminApi = new AdminApi(client);

function StatCard({ label, value, subtitle }: { label: string; value: string | number; subtitle?: string }) {
  return (
    <div
      style={{
        border: "1px solid var(--border-color, #e0e0e0)",
        borderRadius: 8,
        padding: 16,
        minWidth: 160,
        background: "var(--card-bg, #fff)",
      }}
    >
      <div style={{ fontSize: 12, color: "var(--text-secondary, #666)", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, color: "var(--text-primary, #111)" }}>
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11, color: "var(--text-secondary, #999)", marginTop: 4 }}>
          {subtitle}
        </div>
      )}
    </div>
  );
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getStats();
      setStats(data);
      setError(null);
    } catch (e: any) {
      setError(e.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 60_000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return <div style={{ padding: 24 }}>Loading dashboard...</div>;
  }

  if (error && !stats) {
    return <div style={{ padding: 24, color: "red" }}>Error: {error}</div>;
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 24 }}>Platform Dashboard</h1>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
        <StatCard label="Total Users" value={stats?.users_total ?? 0} />
        <StatCard label="Active Users (24h)" value={stats?.users_active_24h ?? 0} />
        <StatCard label="Total Organizations" value={stats?.orgs_total ?? 0} />
        <StatCard
          label="Active Subscriptions"
          value={stats?.subscriptions_active ?? 0}
          subtitle={`${stats?.sponsored_orgs ?? 0} sponsored`}
        />
        <StatCard label="Active Schedules" value={stats?.schedules_active ?? 0} />
      </div>
    </div>
  );
}
